import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { sessionQuery } from './api/session';
import { can, type PermissionCode, type ScopedResource, type Session } from './permissions';

/** The current session. Only valid inside the authenticated layout (it is loaded by its guard). */
export function useSession(): Session {
  return useSuspenseQuery(sessionQuery).data;
}

/** Session if loaded, otherwise null (for components that also render outside the app layout). */
export function useOptionalSession(): Session | null {
  return useQuery({ ...sessionQuery, enabled: false }).data ?? null;
}

export function useCan() {
  const session = useOptionalSession();
  return (code: PermissionCode, resource?: ScopedResource) => can(session, code, resource);
}

export interface CanProps {
  permission: PermissionCode;
  resource?: ScopedResource;
  children: ReactNode;
  /** Rendered when the permission is missing. Defaults to nothing (hidden). */
  fallback?: ReactNode;
}

/** Hides children the user can never use. For "blocked right now", disable with a reason instead. */
export function Can({ permission, resource, children, fallback = null }: CanProps) {
  const check = useCan();
  return <>{check(permission, resource) ? children : fallback}</>;
}
